import React, { useState, useEffect } from 'react';
import {
  DocumentChartBarIcon,
  CalendarIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  TableCellsIcon,
  ClockIcon,
  CogIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  BeakerIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { format, subDays } from 'date-fns';

import { useFormatting } from '../hooks/useFormatting';
import WeeklyReport from './WeeklyReport';
import ReportScheduleModal from './ReportScheduleModal';
import { nutritionApi } from '../api/nutrition';

interface NutritionalReportsHubProps {
  selectedLocation?: string;
  onReportGenerated?: () => void;
}

type ReportType = 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'comparative' | 'compliance' | 'cost-analysis';
type ReportFormat = 'pdf' | 'excel' | 'csv' | 'json';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  type: ReportType;
  icon: React.ComponentType<any>;
  defaultPeriod: string;
  sections: string[];
}

interface Report {
  id: string;
  name: string;
  type: string;
  format: ReportFormat;
  generatedAt: string;
  generatedBy: string;
  size: string;
  status: 'completed' | 'processing' | 'failed';
  downloadUrl?: string;
}

export default function NutritionalReportsHub({ 
  selectedLocation, 
  onReportGenerated 
}: NutritionalReportsHubProps) {
  const { formatCurrency, formatDate } = useFormatting();
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportHistory, setReportHistory] = useState<Report[]>([]);
  const [scheduledReports, setScheduledReports] = useState<any[]>([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    location: selectedLocation || '',
    format: 'pdf' as ReportFormat
  });

  const reportTemplates: ReportTemplate[] = [
    {
      id: 'weekly',
      name: 'Weekly Summary',
      description: 'Comprehensive weekly nutritional analysis and feed mix performance',
      type: 'weekly',
      icon: CalendarIcon,
      defaultPeriod: '7days',
      sections: ['Summary', 'Daily Breakdown', 'Nutritional Trends', 'Cost Analysis', 'Recommendations']
    },
    {
      id: 'monthly',
      name: 'Monthly Report',
      description: 'Detailed monthly nutritional performance and trend analysis',
      type: 'monthly',
      icon: DocumentChartBarIcon,
      defaultPeriod: '30days',
      sections: ['Executive Summary', 'Nutritional Metrics', 'Cost Trends', 'Quality Analysis', 'Compliance Status']
    },
    {
      id: 'quarterly',
      name: 'Quarterly Analysis',
      description: 'Quarterly performance review with seasonal analysis and forecasting',
      type: 'quarterly',
      icon: ArrowTrendingUpIcon,
      defaultPeriod: '90days',
      sections: ['Performance Overview', 'Seasonal Patterns', 'Cost Optimization', 'Goal Progress', 'Strategic Recommendations']
    },
    {
      id: 'yearly',
      name: 'Annual Report',
      description: 'Comprehensive yearly analysis with benchmarking and strategic insights',
      type: 'yearly',
      icon: ChartBarIcon,
      defaultPeriod: '365days',
      sections: ['Annual Summary', 'Trend Analysis', 'Benchmarking', 'ROI Analysis', 'Strategic Planning']
    },
    {
      id: 'comparative',
      name: 'Comparative Analysis',
      description: 'Compare performance across different time periods or locations',
      type: 'comparative',
      icon: TableCellsIcon,
      defaultPeriod: 'custom',
      sections: ['Period Comparison', 'Location Analysis', 'Performance Metrics', 'Variance Analysis']
    },
    {
      id: 'compliance',
      name: 'Compliance Report',
      description: 'Nutritional compliance and regulatory adherence report',
      type: 'compliance',
      icon: CheckCircleIcon,
      defaultPeriod: '30days',
      sections: ['Compliance Status', 'Nutritional Standards', 'Quality Metrics', 'Audit Trail', 'Corrective Actions']
    },
    {
      id: 'cost-analysis',
      name: 'Cost Analysis',
      description: 'Detailed cost breakdown and optimization opportunities',
      type: 'cost-analysis',
      icon: CurrencyDollarIcon,
      defaultPeriod: '30days',
      sections: ['Cost Summary', 'Component Analysis', 'Trend Analysis', 'Optimization Opportunities', 'Budget Variance']
    }
  ];

  useEffect(() => {
    loadReportHistory();
    loadScheduledReports();
  }, [selectedLocation]);

  const loadReportHistory = async () => {
    try {
      const response = await nutritionApi.getReportHistory({
        location: reportFilters.location,
        startDate: reportFilters.startDate,
        endDate: reportFilters.endDate
      });
      setReportHistory(response.data as Report[]);
    } catch (error) {
      console.error('Failed to load report history:', error);
    }
  };

  const loadScheduledReports = async () => {
    try {
      const response = await nutritionApi.getScheduledReports({
        location: reportFilters.location
      });
      setScheduledReports(response.data as any[]);
    } catch (error) {
      console.error('Failed to load scheduled reports:', error);
    }
  };

  const handleGenerateReport = async (template: ReportTemplate) => {
    try {
      setIsGenerating(true);
      const response = await nutritionApi.generateReport({
        type: template.type,
        format: reportFilters.format,
        startDate: reportFilters.startDate,
        endDate: reportFilters.endDate,
        location: reportFilters.location
      });
      
      const responseData = response.data as { downloadUrl?: string };
      if (responseData.downloadUrl) {
        window.open(responseData.downloadUrl, '_blank');
      }
      
      await loadReportHistory();
      onReportGenerated?.();
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleScheduleReport = async (scheduleData: any) => {
    try {
      await nutritionApi.scheduleReport({
        ...scheduleData,
        location: reportFilters.location
      });
      await loadScheduledReports();
      setShowScheduleModal(false);
    } catch (error) {
      console.error('Failed to schedule report:', error);
    }
  };

  const getReportIcon = (type: string) => {
    const template = reportTemplates.find(t => t.type === type);
    return template?.icon || DocumentChartBarIcon;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-100 text-emerald-800';
      case 'processing':
        return 'bg-amber-100 text-amber-800';
      case 'failed':
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <DocumentChartBarIcon className="h-6 w-6 text-cyan-600 mr-2" />
            <h2 className="text-lg font-medium text-slate-900">Nutritional Reports</h2>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowScheduleModal(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
            >
              <ClockIcon className="h-4 w-4 mr-2" />
              Schedule Reports
            </button>
            <button
              onClick={loadReportHistory}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Report Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-slate-50/80 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
            <input
              type="date"
              value={reportFilters.startDate}
              onChange={(e) => setReportFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
            <input
              type="date"
              value={reportFilters.endDate}
              onChange={(e) => setReportFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
            <select
              value={reportFilters.location}
              onChange={(e) => setReportFilters(prev => ({ ...prev, location: e.target.value }))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option key="empty-option-1753461297335" value="">All Locations</option>
              <option key="Main Aviary-1753461297335" value="Main Aviary">Main Aviary</option>
              <option key="Breeding Area-1753461297335" value="Breeding Area">Breeding Area</option>
              <option key="Quarantine-1753461297335" value="Quarantine">Quarantine</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Format</label>
            <select
              value={reportFilters.format}
              onChange={(e) => setReportFilters(prev => ({ ...prev, format: e.target.value as ReportFormat }))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option key="pdf-1753461297335" value="pdf">PDF</option>
              <option key="excel-1753461297335" value="excel">Excel</option>
              <option key="csv-1753461297335" value="csv">CSV</option>
              <option key="json-1753461297335" value="json">JSON</option>
            </select>
          </div>
        </div>
      </div>

      {/* Report Templates */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reportTemplates.map((template) => {
          const IconComponent = template.icon;
          return (
            <div key={template.id} className="bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <IconComponent className="h-8 w-8 text-cyan-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-slate-900">{template.name}</h3>
                    <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Includes:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  {template.sections.map((section, index) => (
                    <li key={index} className="flex items-center">
                      <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full mr-2"></div>
                      {section}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  Default: {template.defaultPeriod}
                </span>
                <button
                  onClick={() => handleGenerateReport(template)}
                  disabled={isGenerating}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Weekly Report Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Weekly Report Preview</h3>
        <WeeklyReport 
          selectedLocation={selectedLocation}
          onReportGenerated={onReportGenerated}
        />
      </div>

      {/* Report History */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-slate-900">Recent Reports</h3>
        </div>
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Report
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Generated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportHistory.map((report) => {
                const IconComponent = getReportIcon(report.type);
                return (
                  <tr key={report.id} className="hover:bg-slate-50/80">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <IconComponent className="h-5 w-5 text-slate-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-slate-900">{report.name}</div>
                          <div className="text-sm text-slate-500">{report.format.toUpperCase()}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 capitalize">
                        {report.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div>{formatDate(new Date(report.generatedAt))}</div>
                      <div className="text-xs text-slate-500">by {report.generatedBy}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {report.size}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                        {report.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button className="text-cyan-600 hover:text-indigo-900 mr-3">
                        Download
                      </button>
                      <button className="text-slate-600 hover:text-slate-900">
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scheduled Reports */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900">Scheduled Reports</h3>
            <button
              onClick={() => setShowScheduleModal(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
            >
              <ClockIcon className="h-4 w-4 mr-2" />
              Add Schedule
            </button>
          </div>
        </div>
        <div className="p-6">
          {scheduledReports.length > 0 ? (
            <div className="space-y-4">
              {scheduledReports.map((schedule) => (
                <div key={schedule.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-slate-900">{schedule.name}</h4>
                      <p className="text-sm text-slate-500">
                        {schedule.frequency} • {schedule.format.toUpperCase()} • {schedule.recipients.length} recipients
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Next run: {formatDate(new Date(schedule.nextRun))}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        schedule.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-violet-100 text-violet-800'
                      }`}>
                        {schedule.enabled ? 'Active' : 'Disabled'}
                      </span>
                      <button className="text-slate-400 hover:text-slate-600">
                        <CogIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ClockIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No scheduled reports</h3>
              <p className="mt-1 text-sm text-slate-500">
                Set up automated report generation to receive regular updates.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Report Schedule Modal */}
      <ReportScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSchedule={handleScheduleReport}
      />
    </div>
  );
} 